/**
 * LeadDetail Page
 * Apple-quality design with true black dark mode
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon,
  SparklesIcon,
  ExclamationCircleIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import leadIntakeService from '../../services/LeadIntakeService';
import LeadScoreBadge from '../../components/leads/LeadScoreBadge';
import NurtureStatus from '../../components/leads/NurtureStatus';
import toast from 'react-hot-toast';

const STATUSES = [
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'proposal', label: 'Proposal' },
  { id: 'won', label: 'Won' },
  { id: 'lost', label: 'Lost' }
];

const LeadDetail = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const { leadId } = useParams();

  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingFollowUp, setSendingFollowUp] = useState(false);

  useEffect(() => {
    if (currentUser?.uid && leadId) {
      loadLead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, leadId]);

  const loadLead = async () => {
    try {
      const res = await leadIntakeService.getLead(currentUser.uid, leadId);
      setLead(res.lead);
      setForm(res.form);
    } catch (error) {
      toast.error('Failed to load lead');
      navigate('/app/leads');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      await leadIntakeService.updateLeadStatus(leadId, status, currentUser.uid);
      setLead(prev => ({ ...prev, status }));
      toast.success('Status updated');
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendFollowUp = async () => {
    setSendingFollowUp(true);
    try {
      await leadIntakeService.sendFollowUp(leadId, currentUser.uid);
      setLead(prev => ({ ...prev, followUpSent: true }));
      toast.success('Follow-up sent');
    } catch (error) {
      toast.error('Failed to send follow-up');
    } finally {
      setSendingFollowUp(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-black flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-brand border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-black">
      {/* Header */}
      <div className="bg-white/80 dark:bg-black/80 backdrop-blur-xl border-b border-black/5 dark:border-white/5 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-5">
          <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate('/app/leads')}
              className="p-2 -ml-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-black/50 dark:text-white/50" />
            </button>
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <h1 className="text-[24px] font-semibold text-black dark:text-white">
                  {lead.name || 'Unnamed Lead'}
                </h1>
                <LeadScoreBadge score={lead.aiScore} temperature={lead.temperature} size="md" />
              </div>
              <p className="text-[13px] text-black/50 dark:text-white/50 mt-0.5">
                From {form?.name || 'Unknown Form'}
              </p>
            </div>
          </div>

          {/* Status Pills */}
          <div className="flex gap-2">
            {STATUSES.map((status) => (
              <button
                key={status.id}
                onClick={() => handleStatusChange(status.id)}
                className={`px-3 py-1.5 rounded-full text-[13px] font-medium transition-all ${
                  lead.status === status.id
                    ? 'bg-black dark:bg-white text-white dark:text-black'
                    : 'bg-black/5 dark:bg-white/5 text-black/50 dark:text-white/50 hover:bg-black/10 dark:hover:bg-white/10'
                }`}
              >
                {status.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* AI Analysis */}
            {lead.aiAnalysis && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-brand/10 to-orange-500/5
                  rounded-2xl p-6 border border-brand/20"
              >
                <div className="flex items-center gap-2 mb-4">
                  <SparklesIcon className="w-5 h-5 text-brand" />
                  <h2 className="text-[15px] font-semibold text-black dark:text-white">AI Analysis</h2>
                </div>

                <p className="text-[15px] text-black/70 dark:text-white/70 mb-4">
                  {lead.aiAnalysis.summary}
                </p>

                {lead.aiAnalysis.strengths?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[13px] font-medium text-green-600 dark:text-green-400 mb-2">
                      Strengths
                    </p>
                    <ul className="space-y-1.5">
                      {lead.aiAnalysis.strengths.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-black/60 dark:text-white/60">
                          <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {lead.aiAnalysis.concerns?.length > 0 && (
                  <div className="mb-4">
                    <p className="text-[13px] font-medium text-amber-600 dark:text-amber-400 mb-2">
                      Concerns
                    </p>
                    <ul className="space-y-1.5">
                      {lead.aiAnalysis.concerns.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-[13px] text-black/60 dark:text-white/60">
                          <ExclamationCircleIcon className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {lead.aiAnalysis.recommendedAction && (
                  <div className="flex items-start gap-2 p-3 bg-white/50 dark:bg-black/20 rounded-xl">
                    <LightBulbIcon className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[13px] font-medium text-black dark:text-white">Recommended</p>
                      <p className="text-[13px] text-black/60 dark:text-white/60">
                        {lead.aiAnalysis.recommendedAction}
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            )}

            {/* Responses */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10"
            >
              <h2 className="text-[15px] font-semibold text-black dark:text-white mb-4">Responses</h2>

              <div className="space-y-4">
                {form?.questions?.map((question) => {
                  const response = lead.responses?.[question.id];
                  if (!response) return null;

                  return (
                    <div key={question.id} className="pb-4 border-b border-black/5 dark:border-white/10 last:border-0 last:pb-0">
                      <p className="text-[13px] font-medium text-black/50 dark:text-white/50 mb-1">
                        {question.label}
                      </p>
                      <p className="text-[15px] text-black dark:text-white">
                        {Array.isArray(response) ? response.join(', ') : response}
                      </p>
                    </div>
                  );
                })}
              </div>
            </motion.div>

            {/* Activity */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10"
            >
              <h2 className="text-[15px] font-semibold text-black dark:text-white mb-4">Activity</h2>

              <div className="space-y-4">
                {(lead.activities || []).map((activity, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      activity.type === 'form_submitted' ? 'bg-blue-500/10' :
                      activity.type === 'ai_scored' ? 'bg-purple-500/10' :
                      activity.type === 'followup_sent' ? 'bg-green-500/10' :
                      'bg-black/5 dark:bg-white/5'
                    }`}>
                      {activity.type === 'form_submitted' && <EnvelopeIcon className="w-4 h-4 text-blue-500" />}
                      {activity.type === 'ai_scored' && <SparklesIcon className="w-4 h-4 text-purple-500" />}
                      {activity.type === 'followup_sent' && <PaperAirplaneIcon className="w-4 h-4 text-green-500" />}
                      {activity.type === 'status_changed' && <ClockIcon className="w-4 h-4 text-black/40 dark:text-white/40" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-[14px] text-black dark:text-white">
                        {activity.type === 'form_submitted' && 'Form submitted'}
                        {activity.type === 'ai_scored' && `Scored ${activity.details?.score}/100 (${activity.details?.temperature})`}
                        {activity.type === 'followup_sent' && 'Follow-up sent'}
                        {activity.type === 'status_changed' && `Status: ${activity.details?.from} → ${activity.details?.to}`}
                      </p>
                      <p className="text-[12px] text-black/40 dark:text-white/40">
                        {formatDate(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10"
            >
              <h2 className="text-[15px] font-semibold text-black dark:text-white mb-4">Contact</h2>

              <div className="space-y-2">
                {lead.email && (
                  <a
                    href={`mailto:${lead.email}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-black/[0.02] dark:bg-white/5
                      hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors group"
                  >
                    <EnvelopeIcon className="w-4 h-4 text-black/30 dark:text-white/30 group-hover:text-brand" />
                    <span className="text-[14px] text-black/70 dark:text-white/70 truncate">{lead.email}</span>
                  </a>
                )}
                {lead.phone && (
                  <a
                    href={`tel:${lead.phone}`}
                    className="flex items-center gap-3 p-3 rounded-xl bg-black/[0.02] dark:bg-white/5
                      hover:bg-black/[0.05] dark:hover:bg-white/10 transition-colors group"
                  >
                    <PhoneIcon className="w-4 h-4 text-black/30 dark:text-white/30 group-hover:text-brand" />
                    <span className="text-[14px] text-black/70 dark:text-white/70">{lead.phone}</span>
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="mt-4 pt-4 border-t border-black/5 dark:border-white/10 space-y-2">
                {!lead.followUpSent ? (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={handleSendFollowUp}
                    disabled={sendingFollowUp}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-brand text-white
                      rounded-xl text-[14px] font-semibold disabled:opacity-50"
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                    {sendingFollowUp ? 'Sending...' : 'Send Follow-up'}
                  </motion.button>
                ) : (
                  <div className="flex items-center justify-center gap-2 py-3 text-green-600 dark:text-green-400">
                    <CheckCircleIcon className="w-4 h-4" />
                    <span className="text-[14px] font-medium">Follow-up sent</span>
                  </div>
                )}
                <a
                  href={`mailto:${lead.email}`}
                  className="w-full flex items-center justify-center gap-2 py-3
                    border border-black/10 dark:border-white/10 text-black/70 dark:text-white/70
                    rounded-xl text-[14px] font-medium hover:bg-black/5 dark:hover:bg-white/5"
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  Send Email
                </a>
              </div>
            </motion.div>

            {/* Nurture Status */}
            <NurtureStatus
              lead={lead}
              userId={currentUser?.uid}
              onRefresh={loadLead}
            />

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10"
            >
              <h2 className="text-[15px] font-semibold text-black dark:text-white mb-4">Details</h2>

              <div className="space-y-3 text-[14px]">
                <div className="flex justify-between">
                  <span className="text-black/50 dark:text-white/50">Submitted</span>
                  <span className="text-black dark:text-white">{formatDate(lead.submittedAt)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-black/50 dark:text-white/50">Score</span>
                  <span className="text-black dark:text-white font-semibold">{lead.aiScore}/100</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-black/50 dark:text-white/50">Temperature</span>
                  <span className="text-black dark:text-white capitalize">{lead.temperature}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetail;
